import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { Button } from "@heroui/react";
import { useCallback, useMemo } from "react";
import Joyride, {
  CallBackProps,
  EVENTS,
  TooltipRenderProps,
} from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";
import { setTour } from "../store/user/reducer";
import { api } from "@/api";

function Tooltip({
  backProps,
  index,
  isLastStep,
  primaryProps,
  step,
  tooltipProps,
}: TooltipRenderProps) {
  return (
    <div
      {...tooltipProps}
      className="flex flex-col gap-4 bg-background dark:bg-zinc-900 rounded-xl border-2 border-primary p-4">
      <span className="text-foreground font-medium max-w-64">
        {step.content}
      </span>
      <div className="flex flex-row gap-2">
        {/* @ts-ignore */}
        {index > 0 && (
          <Button
            color="default"
            variant="flat"
            // @ts-ignore
            onClick={(e) => backProps.onClick(e)}>
            Назад
          </Button>
        )}
        {/* @ts-ignore */}
        {!isLastStep && (
          <Button
            color="primary"
            variant="flat"
            // @ts-ignore
            onClick={(e) => primaryProps.onClick(e)}>
            Вперед
          </Button>
        )}
        {/* @ts-ignore */}
        {isLastStep && (
          <Button
            color="primary"
            variant="flat"
            // @ts-ignore
            onClick={(e) => backProps.onClick(e)}>
            Закрыть
          </Button>
        )}
      </div>
    </div>
  );
}

export function Tour() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const { tour } = useAppSelector((state) => state.user);
  const currentUser = useAppSelector((state) => state.user);
  const currentLocation = useAppSelector((state) => state.location);

  const isInitialTour = location.pathname === "/dashboard/selectLocation";

  const initialSteps = [
    {
      target: "#companies-selector-title",
      content:
        "Ниже отображен список компаний, которые привязаны к вашему аккаунту. Вы можете выбрать любую из них, чтобы перейти к работе с ней.",
      data: {},
    },
    {
      target: "#create-renter-button",
      content:
        "Если вы хотите добавить новую компанию, вы можете воспользоваться кнопкой создания.",
      data: {},
    },
    {
      target: "#companies-selector-title",
      content: "Выберите любую компанию для продолжения тура.",
      data: {},
    },
  ];

  const platformSteps = useMemo(() => {
    return [
      {
        target: ".greetings",
        content: "Добро пожаловать на платформу! Давайте осмотримся поближе.",
        data: {},
      },
      {
        target: ".locks",
        content:
          "Тут расположены замки, к которым вы имеете доступ. Вы можете открывать их одним нажатием на карточку замка.",
        data: {},
      },
      {
        target: ".rent-meeting-room",
        content:
          'Тут вы можете перейти к брони переговорок - давайте так и сделаем. Нажмите на кнопку "Вперёд".',
        data: {
          next: "/dashboard/meetingRooms",
        },
      },
      {
        target: "#meeting-rooms-title",
        content:
          "Это список переговорных комнат, которые доступны для бронирования. Вы можете просматривать их и бронировать.",
        data: {
          previous: "/dashboard",
        },
      },
      {
        target: "#my-bookings-title",
        content:
          "Ниже будет список Ваших бронирований. Вы можете просматривать их и отменять.",
        data: {
          next: "/dashboard/tickets",
        },
      },
      {
        target: "#tickets-title",
        content:
          "Добро пожаловать в тикеты! Это, своего рода, обращения в поддержку коворкинга.",
        data: {
          previous: "/dashboard/meetingRooms",
        },
      },
      {
        target: "#create-ticket-button",
        content:
          "Вы можете создать тикет, если у вас есть какие-либо вопросы или проблемы.",
        data: {
          next: "/dashboard/printers",
        },
      },
      {
        target: "#printers-title",
        content: "Не менее интересный раздел платформы - принтеры.",
        data: {
          previous: "/dashboard/tickets",
        },
      },
      {
        target: "#printers-list",
        content:
          "Вы можете печатать свои документы на принтерах, которые расположены в коворкинге.",
        data: {},
      },
      {
        target: "#available-prints-title",
        content:
          "Не забывайте следить за счётчиком доступных страниц, чтобы не превысить лимит своей компании.",
        data: {
          next: "/dashboard/payments",
        },
      },
      {
        target: "#billings-title",
        content:
          'В разделе "Платежи" вы можете просматривать выставленные Вашей компании счета.',
        data: {
          previous: "/dashboard/printers",
          next: "/dashboard/renters/" + currentLocation.location.id,
        },
      },
      {
        target: "#renter-name",
        content: "На этой странице представлена информация о Вашей компании.",
        data: {
          previous: "/dashboard/billings",
        },
      },
      {
        target: "#renter-actions",
        content:
          "При необходимости, вы можете отредактировать информацию о компании или передать её другому пользователю.",
        data: {
          next: "/dashboard/users",
        },
      },
      {
        target: "#users-title",
        content:
          'В разделе "Пользователи" вы можете просматривать список Ваших сотрудников и управлять ими.',
        data: {
          previous: "/dashboard/renters/" + currentLocation.location.id,
        },
      },
      {
        target: ".role-give",
        content:
          "Нажмите на эту кнопку, чтобы настроить права доступа другому пользователю.",
        data: {
          next: "/dashboard/users/" + currentUser.currentUser?.id + "/roles",
        },
      },
      {
        target: ".roles",
        content: "Тут вы можете настроить права доступа для пользователя.",
        data: {
          previous: "/dashboard/users",
        },
      },
      {
        target: ".actions",
        content: "Тут вы можете выбрать какое-либо действие с пользователем.",
        data: {
          next: "/dashboard/requisites",
        },
      },
      {
        target: "#requisites-title",
        content:
          'В разделе "Реквизиты" вы можете просматривать, создавать и редактировать реквизиты Вашей компании.',
        data: {
          previous:
            "/dashboard/users/" + currentUser.currentUser?.id + "/roles",
          next: "/dashboard/offices",
        },
      },
      {
        target: "#offices-title",
        content:
          'В разделе "Офисы" Вы можете просматривать доступные Вашей компании офисы.',
        data: {
          previous: "/dashboard/requisites",
        },
      },
      {
        target: "#offices-description",
        content:
          "Здесь отображается список всех офисов, доступных вашей компании в выбранной локации.",
        data: {},
      },
      {
        target: "#offices-table",
        content:
          "В таблице вы можете видеть подробную информацию об офисах: название, тип, площадь, цену и количество комнат.",
        data: {},
      },
      {
        target: ".view-lock",
        content: "Тут Вы можете просматривать журнал доступа к замку.",
        data: {},
      },
      {
        target: "#offices-title",
        content:
          "Здесь вы можете управлять замками офисов - открывать и закрывать их удаленно.",
        data: {
          next: "/dashboard",
        },
      },
      {
        target: "#marketplace-link, #marketplace-link-mobile",
        content:
          "Маркетплейс - это отдельная платформа, где вы можете арендовать офисы, переговорные комнаты и другие рабочие пространства.",
        data: {
          previous: "/dashboard/offices",
        },
      },
      {
        target: "#marketplace-link, #marketplace-link-mobile",
        content:
          'Нажмите на "Маркетплейс", чтобы перейти на внешнюю платформу аренды рабочих пространств.',
        data: {},
      },
      {
        target: ".greetings",
        content:
          "На этом знакомство с платформой завершено. Желаем Вам успехов в работе!",
        data: {},
      },
    ];
  }, [currentLocation, currentUser]);

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const {
        action,
        step: {
          data: { next, previous },
        },
        index,
        type,
      } = data;

      const isPreviousAction = action === "prev";
      if (type == EVENTS.STEP_AFTER) {
        if (isPreviousAction && previous) {
          dispatch(setTour({ stepIndex: index - 1 }));
          return navigate(previous);
        } else if (!isPreviousAction && next) {
          dispatch(setTour({ stepIndex: index + 1 }));
          return navigate(next);
        }

        dispatch(setTour({ stepIndex: index + 1 }));
        if (index == platformSteps.length - 1) {
          api.auth.updateInfo({
            tour_completed: true,
          });
        }
      }
    },
    [dispatch, platformSteps.length, navigate],
  );

  return (
    <Joyride
      steps={isInitialTour ? initialSteps : platformSteps}
      continuous
      disableOverlay
      run={tour.run && tour.isTourActive}
      stepIndex={tour.stepIndex}
      callback={handleJoyrideCallback}
      tooltipComponent={Tooltip}
      locale={{
        back: "Назад",
        close: "Закрыть",
        last: "Последний шаг",
        next: "Вперед",
        skip: "Пропустить",
        open: "Открыть диалог",
        nextLabelWithProgress: "Дальше (шаг {step} из {steps})",
      }}
      styles={{
        beaconInner: {
          backgroundColor: "#318aef",
        },
        beaconOuter: {
          backgroundColor: "#318aef",
          border: "none",
        },
      }}
    />
  );
}
